import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CircleIcon from '@mui/icons-material/Circle';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import config from '../../../../../../../../../../config';
import { SessionSummaryFieldsFragment } from '../../../../../../../../../../generated/core-api/graphql-react-query';
import withSessionDetailsSectionErrorBoundary from '../SessionDetailsSectionErrorBoundary';
import useEnableSummary from './Hooks/useEnableSummary';
import useSessionSummary from './Hooks/useSessionSummary';
import StyledBox, { StyledTranscriptAlert } from './styles';

interface SessionResumeProps {
  session: SessionSummaryFieldsFragment;
}

interface SummaryContentProps {
  session: SessionSummaryFieldsFragment;
  sessionSummary: string | null;
  loading: boolean;
  generateSummary: () => void;
}

export function SummaryContent(props: SummaryContentProps) {
  const {
    session, sessionSummary, loading, generateSummary,
  } = props;
  const { t } = useTranslation('session');

  if (sessionSummary) {
    return <ReactMarkdown className="markdown">{sessionSummary}</ReactMarkdown>;
  }
  if (loading) {
    return <CircularProgress />;
  }

  const numCharacters = session.transcriptInfo?.numCharacters ?? 0;
  const numSentences = session.transcriptInfo?.numSentences ?? 0;
  const hasNoTranscript = !numCharacters && !numSentences;
  const canSummarize = session.duration >= config.Live.Audio.MinimumSessionDuration
    && numCharacters >= config.Live.Audio.MinimumCharacters
    && numSentences >= config.Live.Audio.MinimumSentences;

  return (
    <Box className="cta">
      <Typography>{t('details.summary.empty')}</Typography>
      {!canSummarize && (
        <StyledTranscriptAlert severity={hasNoTranscript ? 'warning' : 'info'}>
          <AlertTitle>{t('details.summary.alerts.title')}</AlertTitle>
          <List disablePadding dense>
            {hasNoTranscript ? (
              <ListItem disableGutters>
                <ListItemIcon><CircleIcon /></ListItemIcon>
                {t('details.summary.alerts.no-transcript')}
              </ListItem>
            ) : (
              <>
                {numCharacters < config.Live.Audio.MinimumCharacters && (
                  <ListItem disableGutters>
                    <ListItemIcon><CircleIcon /></ListItemIcon>
                    {t('details.summary.alerts.insufficient-characters', { minCharacters: config.Live.Audio.MinimumCharacters, characters: numCharacters })}
                  </ListItem>
                )}
                {numSentences < config.Live.Audio.MinimumSentences && (
                  <ListItem disableGutters>
                    <ListItemIcon><CircleIcon /></ListItemIcon>
                    {t('details.summary.alerts.insufficient-sentences', { minSentences: config.Live.Audio.MinimumSentences, sentences: numSentences })}
                  </ListItem>
                )}
              </>
            )}
            {session.duration < config.Live.Audio.MinimumSessionDuration && (
              <ListItem disableGutters>
                <ListItemIcon><CircleIcon /></ListItemIcon>
                {t('details.summary.alerts.insufficient-duration', { minDuration: config.Live.Audio.MinimumSessionDuration / 1000, duration: Math.round(session.duration / 1000) })}
              </ListItem>
            )}
          </List>
        </StyledTranscriptAlert>
      )}
      <Button
        variant="outlined"
        color="primary"
        onClick={() => { generateSummary(); }}
        disabled={loading || !canSummarize}
      >
        {t('details.summary.generateSummary')}
      </Button>
    </Box>
  );
}

function SessionSummary(props: SessionResumeProps) {
  const { session } = props;
  const { t } = useTranslation('session');
  const {
    generateSummary, text, loading, error,
  } = useSessionSummary(session.id);

  const enableSummary = useEnableSummary();
  const sessionIdRef = React.useRef<string | null>(null);

  const sessSummary: string | null = React.useMemo(() => {
    if (!session.summary && !text) {
      return null;
    }
    // check if there is a ```markdown content ``` block in the summary or text. If there is remove it and return the cleaned text
    const regex = /```markdown([\s\S]*?)(```)?/gm;
    const summaryToClean = session.summary || text || '';
    const cleanedSummary = summaryToClean.replace(regex, '').trim();
    return cleanedSummary;
  }, [session.summary, text]);

  const canAutoSummarize = session.duration >= config.Live.Audio.MinimumSessionDuration
    && (session.transcriptInfo?.numCharacters ?? 0) >= config.Live.Audio.MinimumCharacters
    && (session.transcriptInfo?.numSentences ?? 0) >= config.Live.Audio.MinimumSentences;

  React.useEffect(() => {
    if (enableSummary && !(session.summary || text) && canAutoSummarize) {
      if (sessionIdRef.current !== session.id) {
        sessionIdRef.current = session.id;
        generateSummary();
      }
    }
  }, [enableSummary, generateSummary, session.summary, canAutoSummarize, text, session.id]);

  if (!enableSummary) { return null; }
  return (
    <StyledBox>
      <Typography variant="subtitle1">
        {t('details.summary.title')}
      </Typography>
      <Box>
        <SummaryContent
          session={session}
          sessionSummary={sessSummary}
          loading={loading}
          generateSummary={generateSummary}
        />
        {(sessSummary) && (
          <CopyToClipboard
            text={sessSummary}
            onCopy={() => {
              enqueueSnackbar(
                t('details.summary.copy-feedback'),
                {
                  style: { width: 300 },
                  autoHideDuration: 6000,
                  variant: 'success',
                  anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                },
              );
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="small"
            >
              <ContentCopyIcon />
              {t('details.summary.copy')}
            </Button>
          </CopyToClipboard>
        )}
      </Box>
      {error && (<Alert severity="error">{error}</Alert>)}
    </StyledBox>
  );
}

export default withSessionDetailsSectionErrorBoundary(SessionSummary, 'session summary');
